import { ApiError } from "@carepass/api-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { downloadAdminFile } from "../../app/files";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication } from "../../app/providers";
import { pathForExportJob } from "../../app/routes";
import { saveBlob } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import { StateNotice } from "../workspace/shared";
import { ExportView } from "./components/ExportView";
import { useExportDetail, useRequestExport } from "./queries";
import { defaultExportRequest, parseExportJobId } from "./schema";
import type { ExportRequestValues } from "./schema";

function detailState(query: ReturnType<typeof useExportDetail>) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

function ExportDetailPageForJob({ jobId }: { readonly jobId: string }) {
  const services = useAdminApplication();
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [pollGeneration, setPollGeneration] = useState(0);
  const query = useExportDetail(jobId, !pollTimedOut);
  const request = useRequestExport();
  const [notice, setNotice] = useState("");
  const fileRequests = useRef(new Set<AbortController>());
  const navigation = useAdminNavigation({
    contextKey: "exports",
    defaultListPath: "/operations/exports?page=0",
    originLabel: "정산 Export 상세",
  });

  useEffect(() => {
    setPollTimedOut(false);
    const timer = window.setTimeout(() => setPollTimedOut(true), services.polling.timeoutMs);
    return () => window.clearTimeout(timer);
  }, [jobId, pollGeneration, services.polling.timeoutMs]);

  useEffect(() => () => {
    for (const controller of fileRequests.current) controller.abort();
    fileRequests.current.clear();
  }, []);

  const retryDetail = () => {
    setPollGeneration((value) => value + 1);
    setPollTimedOut(false);
    void query.refetch();
  };

  const submitRequest = async (values: ExportRequestValues) => {
    const job = await request.mutateAsync(values);
    setNotice(`${job.jobId} 작업을 ${job.status} 상태로 접수했습니다.`);
    navigation.openRelated(pathForExportJob(job.jobId), "정산 Export 상세");
    return job;
  };

  const download = useCallback(async (job: Schemas["DataExportJobView"]) => {
    if (job.status !== "READY" || !job.fileAssetId) throw new Error("The export file is not ready.");
    const controller = new AbortController();
    fileRequests.current.add(controller);
    try {
      const blob = await downloadAdminFile(services, job.fileAssetId, {}, controller.signal);
      saveBlob(blob, `carepass-settlements-${job.from}-${job.to}.csv`);
      setNotice("");
    } finally { fileRequests.current.delete(controller); }
  }, [services]);

  return <ExportView
    mode="detail"
    criteria={{ status: "", exportType: "", requestedFrom: "", requestedTo: "", page: 0, size: 20 }}
    listState="ready"
    selectedJobId={jobId}
    selected={query.data}
    detailState={detailState(query)}
    pollTimedOut={pollTimedOut && ["REQUESTED", "PROCESSING"].includes(query.data?.status ?? "")}
    defaultRequest={defaultExportRequest()}
    notice={notice}
    detailHref={pathForExportJob}
    backHref={navigation.backPath}
    backLabel={navigation.backLabel}
    onBack={navigation.backToList}
    onSearch={() => undefined}
    onPageChange={() => undefined}
    onSelect={() => undefined}
    onClearSelection={navigation.backToList}
    onRetryList={async () => {
      const result = await query.refetch();
      if (result.error) throw result.error;
    }}
    onRetryDetail={retryDetail}
    onRequest={submitRequest}
    onDownload={download}
  />;
}

export function ExportDetailPage() {
  const jobId = parseExportJobId(useParams().jobId ?? null);
  if (!jobId) return <StateNotice state="not-found" />;
  return <ExportDetailPageForJob key={jobId} jobId={jobId} />;
}
